import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  FormInputText,
  InputLabel,
} from '@hopskipdrive/react-components';

import { CREATE_NEW_LOCATION_GROUP, GroupedOption, useLocationOptions } from '../../../../hooks/useLocationOptions';
import { defaultLocationState, SelectLocation } from '../../../../locations/features/MapPins/SelectLocation';
import { PageHeading } from '../../../../shared/components/PageHeading';
import { MustBeMetProtocol } from '../../MustBeMetProtocolForm';
import { Contact } from '../../MustBeMetProtocolForm/types';
import { mustBeMetProtocolValidationSchema } from '../../MustBeMetProtocolForm/validationSchema';
import { MustBeMetLabel } from '../RiderCreationModal.styles';

import { Divider, FlexRow } from './PrimaryResidenceAdress.styles';
import { newLocationSchema } from './validationSchema';

export type LocationData = {
  residence: {
    address: GroupedOption;
    dropoffProcedureInstructions: string;
    locationType: string;
    name: string;
    pickupProcedureInstructions: string;
    streetAddressTwo: string;
  };
  mustBeMet?: boolean;
  mustBeMetProtocol?: MustBeMetProtocol;
};

interface AddDefaultResidenceAddressProps {
  addressDetails: LocationData;
  contacts: Contact[];
  onSubmit: (data) => void;
}

const CreateNewLocationGroupLabel = 'Create a new location:';

export const PrimaryResidenceAddressForm: FC<AddDefaultResidenceAddressProps> = ({
  addressDetails,
  contacts,
  onSubmit,
}) => {
  const [locationId, setLocationId] = useState<string | undefined>(addressDetails.residence.address?.id || undefined);
  const [placeId, setPlaceId] = useState<string | undefined>(addressDetails.residence.address?.placeId || undefined);

  const schema = useMemo(() => newLocationSchema().concat(mustBeMetProtocolValidationSchema()), []);

  const methods = useForm<LocationData>({
    defaultValues: useMemo(() => {
      return addressDetails;
    }, [addressDetails]),
    resolver: yupResolver(schema),
  });
  const {
    handleSubmit,
    control,
    resetField,
    trigger,
    formState: { errors },
  } = methods;
  const { locationOptions, debounceHandleQuery, saveNewLocation } = useLocationOptions();

  const watchAddress = useWatch({
    control,
    name: 'residence.address',
    defaultValue: addressDetails.residence.address,
  });

  const watchMustBeMet = useWatch({
    control,
    name: 'mustBeMet',
    defaultValue: addressDetails.mustBeMet,
  });

  const resetFormFieldsOnAddressChange = () => {
    resetField('mustBeMet');
    resetField('mustBeMetProtocol');
  };

  // Re-trigger validation when schema changes
  useEffect(() => {
    trigger('residence.address');
  }, [schema, trigger]);

  return (
    <form
      id="default-address-form"
      aria-label="form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit((data) => onSubmit(data))();
      }}
    >
      <FlexRow>
        <Address
          saveNewLocation={saveNewLocation}
          locationOptions={locationOptions}
          debounceHandleQuery={debounceHandleQuery}
          control={control}
          handleSelected={(location) => {
            resetFormFieldsOnAddressChange();
            if (location) {
              if (location.group === CREATE_NEW_LOCATION_GROUP) {
                setPlaceId(location.placeId);
                setLocationId(undefined);
              } else {
                setLocationId(location.id);
                setPlaceId(undefined);
              }
            } else {
              setPlaceId(undefined);
              setLocationId(undefined);
            }
          }}
          errors={errors}
        />
      </FlexRow>

      <Divider />

      <Box>
        <PageHeading variant="h2" fontWeight={700} textColor="navy20" sx={{ margin: '15px 0px' }}>
          Instructions for this rider
        </PageHeading>
      </Box>
      <FlexRow>
        <Box display="flex" alignItems="center">
          <Controller
            control={control}
            name="mustBeMet"
            render={({ field: { value, onChange, onBlur } }) => {
              return (
                <Box alignItems="center" mr={6}>
                  <Checkbox
                    id="mustBeMet"
                    data-testid="mustBeMet"
                    onBlur={onBlur}
                    checked={value}
                    onChange={(e) => {
                      onChange(e.target.checked);
                    }}
                    disabled={watchAddress === undefined || watchAddress === null}
                  />
                  <MustBeMetLabel htmlFor="mustBeMet">Rider must be met at drop-off</MustBeMetLabel>
                </Box>
              );
            }}
          />
        </Box>
      </FlexRow>
      <FormProvider {...methods}>
        <MustBeMetProtocol
          mustBeMet={watchMustBeMet}
          accountLocationId={locationId}
          placeId={placeId}
          contacts={contacts}
          defaultValue={addressDetails.mustBeMetProtocol}
        />
      </FormProvider>
    </form>
  );
};

const Address = ({ control, handleSelected, errors, locationOptions, debounceHandleQuery, saveNewLocation }) => {
  const filteredLocationOptions = locationOptions.filter(
    (loc) => loc.locationType === 'residence' || loc.group === CreateNewLocationGroupLabel
  );

  return (
    <Box
      display="flex"
      mt="24px"
      sx={{
        width: { xs: '100%', sm: '80%' },
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
      }}
    >
      <InputLabel htmlFor="default-address">Address</InputLabel>
      <Controller
        control={control}
        name="residence.address"
        render={({ field: { onChange, value } }) => (
          <SelectLocation
            id="default-address"
            hidden={false}
            clearOnSelect={false}
            clearOnCancel={true}
            defaultsForNewLocation={{ ...defaultLocationState, locationType: 'residence' }}
            options={filteredLocationOptions}
            inputValue={value?.label}
            setInputValue={(location) => {
              debounceHandleQuery('', { force: true });
              onChange(location);
              handleSelected(location);

              return location?.label;
            }}
            saveLocation={saveNewLocation}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <FormInputText
                {...params}
                variant="outlined"
                data-testid="address"
                placeholder="Search"
                onChange={(e) => {
                  debounceHandleQuery(e.target.value);
                }}
                error={!!errors.residence?.address}
                helperText={errors.residence?.address && errors.residence?.address.message}
              />
            )}
          />
        )}
      />
    </Box>
  );
};
